import { useState, useEffect, useCallback } from 'react';
import { api, fmt } from '../api';
import toast from 'react-hot-toast';
import NavIcon from '../components/NavIcon';

// ─── Helpers ──────────────────────────────────────────────────────────────────
function toISO(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function isSameLocalDay(a, b) {
  return a.getFullYear() === b.getFullYear() &&
         a.getMonth()    === b.getMonth()    &&
         a.getDate()     === b.getDate();
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS   = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

function Dot({ color }) {
  return <span style={{ width:5,height:5,borderRadius:'50%',background:color,display:'inline-block',flexShrink:0 }} />;
}

// ─── Shared micro-styles ──────────────────────────────────────────────────────
const rowStyle = {
  display:'flex', justifyContent:'space-between', alignItems:'center',
  padding:'8px 10px', background:'var(--surface2)', borderRadius:6, marginBottom:6,
};
const cardStyle = { background:'var(--surface2)', borderRadius:8, padding:'12px 14px', marginBottom:12 };

function MiniTable({ cols, rows }) {
  return (
    <table style={{ width:'100%', fontSize:12, borderCollapse:'collapse' }}>
      <thead>
        <tr>{cols.map((c,i)=>(
          <th key={c} style={{ textAlign:i===0?'left':'right', color:'var(--muted)', fontWeight:600, paddingBottom:4 }}>{c}</th>
        ))}</tr>
      </thead>
      <tbody>
        {rows.map((row,ri)=>(
          <tr key={ri}>{row.map((cell,ci)=>(
            <td key={ci} style={{ textAlign:ci===0?'left':'right', paddingBottom:3, fontWeight:ci===row.length-1?600:400 }}>{cell}</td>
          ))}</tr>
        ))}
      </tbody>
    </table>
  );
}

// ─── Day Detail Popup ─────────────────────────────────────────────────────────
function DayPopup({ date, onClose, onChanged }) {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [reminderText, setReminderText] = useState('');
  const [saving, setSaving]     = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await api.getCalendarDay(toISO(date)); setData(res); }
    catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [date]);

  useEffect(() => { load(); }, [load]);

  const addReminder = async (e) => {
    e.preventDefault();
    const text = reminderText.trim();
    if (!text) return;
    setSaving(true);
    try {
      await api.createReminder(toISO(date), text);
      setReminderText('');
      toast.success('Reminder saved');
      await load(); onChanged();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const deleteReminder = async (id) => {
    try { await api.deleteReminder(id); toast.success('Reminder removed'); await load(); onChanged(); }
    catch (err) { toast.error(err.message); }
  };

  const isToday = isSameLocalDay(date, new Date());
  const tabs = data ? [
    { id:'overview',  label:'Overview' },
    { id:'sales',     label:`Sales (${data.sales.length})` },
    { id:'purchases', label:`Purchases (${data.purchases.length})` },
    { id:'reminders', label:`Reminders (${data.reminders.length})` },
  ] : [];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth:600, width:'95vw', padding:0, overflow:'hidden' }} onClick={e=>e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header" style={{ padding:'14px 20px' }}>
          <div>
            <h3 style={{ margin:0, fontSize:15 }}>
              {date.toLocaleDateString('en-GB',{ weekday:'long', day:'numeric', month:'long', year:'numeric' })}
            </h3>
            {isToday && <span className="badge badge-green" style={{ fontSize:10, marginTop:4, display:'inline-block' }}>Today</span>}
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {loading ? (
          <div style={{ textAlign:'center', padding:48 }}><div className="spinner" /></div>
        ) : !data ? (
          <div style={{ padding:24, color:'var(--muted)', textAlign:'center' }}>Failed to load.</div>
        ) : (<>
          {/* Summary strip */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', borderBottom:'1px solid var(--border)' }}>
            {[
              { label:'Sales Revenue',  value:fmt.currency(data.salesTotal),     sub:`${data.sales.length} tx`,     color:'var(--accent2)' },
              { label:'Purchases Cost', value:fmt.currency(data.purchasesTotal), sub:`${data.purchases.length} tx`, color:'var(--warn)' },
              { label:'Reminders',      value:data.reminders.length,             sub:'set for this day',            color:'var(--accent)' },
            ].map((s,i) => (
              <div key={s.label} style={{ padding:'11px 14px', background:'var(--surface2)', borderRight:i<2?'1px solid var(--border)':'none' }}>
                <div style={{ fontSize:10, color:'var(--muted)', marginBottom:2, textTransform:'uppercase', letterSpacing:'0.05em' }}>{s.label}</div>
                <div style={{ fontSize:15, fontWeight:700, color:s.color }}>{s.value}</div>
                <div style={{ fontSize:11, color:'var(--muted)', marginTop:1 }}>{s.sub}</div>
              </div>
            ))}
          </div>

          {/* Tabs */}
          <div style={{ display:'flex', borderBottom:'1px solid var(--border)' }}>
            {tabs.map(tab => (
              <button key={tab.id} onClick={()=>setActiveTab(tab.id)} style={{
                flex:1, padding:'8px 4px', fontSize:11,
                fontWeight:activeTab===tab.id?600:400,
                color:activeTab===tab.id?'var(--accent)':'var(--muted)',
                background:'none', border:'none',
                borderBottom:activeTab===tab.id?'2px solid var(--accent)':'2px solid transparent',
                cursor:'pointer', whiteSpace:'nowrap',
              }}>{tab.label}</button>
            ))}
          </div>

          {/* Tab content */}
          <div style={{ maxHeight:360, overflowY:'auto', padding:'14px 18px' }}>
            {activeTab === 'overview' && (
              <div>
                {data.sales.length===0 && data.purchases.length===0 && data.reminders.length===0
                  ? <div className="empty" style={{ padding:32 }}>No activity on this day.</div>
                  : <>
                    {data.sales.length>0 && <section style={{ marginBottom:14 }}>
                      <div className="section-title" style={{ marginBottom:8, display:'flex', alignItems:'center', gap:6 }}><NavIcon name="sales" size={14} /> Sales</div>
                      {data.sales.map(s=>(
                        <div key={s.id} style={rowStyle}>
                          <div><span style={{ fontSize:11,color:'var(--muted)',marginRight:6 }}>#{s.id}</span>
                            <span style={{ fontSize:13 }}>{s.items.length} item{s.items.length!==1?'s':''}{s.note?` · ${s.note}`:''}</span></div>
                          <span style={{ fontWeight:600,color:'var(--accent2)',fontSize:13 }}>{fmt.currency(s.total)}</span>
                        </div>
                      ))}
                    </section>}
                    {data.purchases.length>0 && <section style={{ marginBottom:14 }}>
                      <div className="section-title" style={{ marginBottom:8, display:'flex', alignItems:'center', gap:6 }}><NavIcon name="purchases" size={14} /> Purchases</div>
                      {data.purchases.map(p=>(
                        <div key={p.id} style={rowStyle}>
                          <div><span style={{ fontSize:11,color:'var(--muted)',marginRight:6 }}>#{p.id}</span>
                            <span style={{ fontSize:13 }}>{p.supplierName?`${p.supplierName} · `:''}{p.items.length} item{p.items.length!==1?'s':''}</span></div>
                          <span style={{ fontWeight:600,color:'var(--warn)',fontSize:13 }}>{fmt.currency(p.total)}</span>
                        </div>
                      ))}
                    </section>}
                    {data.reminders.length>0 && <section>
                      <div className="section-title" style={{ marginBottom:8, display:'flex', alignItems:'center', gap:6 }}><NavIcon name="bell" size={14} /> Reminders</div>
                      {data.reminders.map(r=>(
                        <div key={r.id} style={{ ...rowStyle, borderLeft:'3px solid var(--accent)' }}>
                          <span style={{ fontSize:13 }}>{r.text}</span>
                          <button onClick={()=>deleteReminder(r.id)} style={{ background:'none',border:'none',cursor:'pointer',color:'var(--muted)',fontSize:13,padding:'2px 6px' }}>✕</button>
                        </div>
                      ))}
                    </section>}
                  </>}
              </div>
            )}
            {activeTab === 'sales' && (
              <div>{data.sales.length===0
                ? <div className="empty" style={{ padding:32 }}>No sales on this day.</div>
                : data.sales.map(s=>(
                  <div key={s.id} style={cardStyle}>
                    <div style={{ display:'flex',justifyContent:'space-between',marginBottom:8 }}>
                      <span style={{ fontWeight:600 }}>Sale #{s.id}</span>
                      <span style={{ fontWeight:700,color:'var(--accent2)' }}>{fmt.currency(s.total)}</span>
                    </div>
                    {s.note && <div style={{ fontSize:12,color:'var(--muted)',marginBottom:8 }}>{s.note}</div>}
                    <MiniTable cols={['Product','Qty','Unit Price','Total']} rows={s.items.map(i=>[i.productName,i.quantity,fmt.currency(i.unitPrice),fmt.currency(i.total)])} />
                  </div>
                ))}
              </div>
            )}
            {activeTab === 'purchases' && (
              <div>{data.purchases.length===0
                ? <div className="empty" style={{ padding:32 }}>No purchases on this day.</div>
                : data.purchases.map(p=>(
                  <div key={p.id} style={cardStyle}>
                    <div style={{ display:'flex',justifyContent:'space-between',marginBottom:8 }}>
                      <span style={{ fontWeight:600 }}>Purchase #{p.id}{p.supplierName&&<span style={{ fontWeight:400,color:'var(--muted)',marginLeft:8 }}>· {p.supplierName}</span>}</span>
                      <span style={{ fontWeight:700,color:'var(--warn)' }}>{fmt.currency(p.total)}</span>
                    </div>
                    {p.note && <div style={{ fontSize:12,color:'var(--muted)',marginBottom:8 }}>{p.note}</div>}
                    <MiniTable cols={['Product','Qty','Unit Cost','Total']} rows={p.items.map(i=>[i.productName,i.quantity,fmt.currency(i.unitCost),fmt.currency(i.total)])} />
                  </div>
                ))}
              </div>
            )}
            {activeTab === 'reminders' && (
              <div>
                <form onSubmit={addReminder} style={{ display:'flex',gap:8,marginBottom:14 }}>
                  <input className="input" style={{ flex:1,fontSize:13 }} placeholder="Add a reminder for this day…"
                    value={reminderText} onChange={e=>setReminderText(e.target.value)} maxLength={200} autoFocus />
                  <button type="submit" className="btn btn-primary" style={{ fontSize:13,whiteSpace:'nowrap' }} disabled={saving||!reminderText.trim()}>
                    {saving?'…':'+ Add'}
                  </button>
                </form>
                {data.reminders.length===0
                  ? <div className="empty" style={{ padding:24 }}>No reminders for this day yet.</div>
                  : data.reminders.map(r=>(
                    <div key={r.id} style={{ display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 12px',background:'var(--surface2)',borderRadius:6,marginBottom:8,borderLeft:'3px solid var(--accent)' }}>
                      <span style={{ fontSize:13 }}>{r.text}</span>
                      <button className="btn-ghost" style={{ fontSize:12,padding:'3px 10px',color:'var(--danger)',marginLeft:8 }} onClick={()=>deleteReminder(r.id)}>Delete</button>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </>)}
      </div>
    </div>
  );
}

// ─── Main Calendar Page ───────────────────────────────────────────────────────
export default function Calendar() {
  const today = new Date();
  const [year,         setYear]         = useState(today.getFullYear());
  const [month,        setMonth]        = useState(today.getMonth());
  const [indicators,   setIndicators]   = useState({});
  const [loadingMonth, setLoadingMonth] = useState(true);
  const [selectedDay,  setSelectedDay]  = useState(null);

  // Right-panel: today's reminders
  const [todayReminders,        setTodayReminders]        = useState([]);
  const [newReminderText,       setNewReminderText]       = useState('');
  const [addingTodayReminder,   setAddingTodayReminder]   = useState(false);

  const loadMonth = useCallback(async (y, m) => {
    setLoadingMonth(true);
    try {
      const res = await api.getCalendarMonth(y, m + 1);
      const ind = {};
      (res.salesDays     || []).forEach(d => { if (!ind[d]) ind[d] = {}; ind[d].hasSales     = true; });
      (res.purchasesDays || []).forEach(d => { if (!ind[d]) ind[d] = {}; ind[d].hasPurchases = true; });
      (res.remindersDays || []).forEach(d => { if (!ind[d]) ind[d] = {}; ind[d].hasReminders = true; });
      setIndicators(ind);
    } catch (e) { toast.error(e.message); }
    finally { setLoadingMonth(false); }
  }, []);

  const loadTodayReminders = useCallback(async () => {
    try {
      const res = await api.getTodayReminders();
      setTodayReminders(Array.isArray(res) ? res : []);
    } catch {
      setTodayReminders([]);
    }
  }, []);

  useEffect(() => { loadMonth(year, month); }, [year, month, loadMonth]);
  useEffect(() => { loadTodayReminders(); }, [loadTodayReminders]);

  const handleAddTodayReminder = async (e) => {
    e.preventDefault();
    const text = newReminderText.trim();
    if (!text) return;
    setAddingTodayReminder(true);
    try {
      await api.createReminder(toISO(today), text);
      setNewReminderText('');
      toast.success('Reminder added');
      loadTodayReminders();
      loadMonth(year, month);
    } catch (err) { toast.error(err.message); }
    finally { setAddingTodayReminder(false); }
  };

  const handleDeleteTodayReminder = async (id) => {
    try {
      await api.deleteReminder(id);
      setTodayReminders(rs => rs.filter(r => r.id !== id));
      loadMonth(year, month);
    } catch (err) { toast.error(err.message); }
  };

  const prevMonth = () => { if (month===0){setYear(y=>y-1);setMonth(11);}else setMonth(m=>m-1); };
  const nextMonth = () => { if (month===11){setYear(y=>y+1);setMonth(0);}else setMonth(m=>m+1); };
  const goToday   = () => { setYear(today.getFullYear()); setMonth(today.getMonth()); };

  const firstDayOfWeek = new Date(year, month, 1).getDay();
  const daysInMonth    = new Date(year, month + 1, 0).getDate();

  return (
    <div style={{ height: '100%' }}>
      <div className="page-header">
        <h2>Calendar</h2>
        <p>View daily sales &amp; purchases — click any date to see details or manage reminders</p>
      </div>

      {/* ── Two-column layout: calendar + right sidebar ── */}
      <div className="calendar-layout">

        {/* ── LEFT: Calendar card (fills all remaining width) ── */}
        <div className="card" style={{ minWidth:0 }}>
          {/* Month nav */}
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <button className="btn-ghost" style={{ padding:'4px 12px', fontSize:17 }} onClick={prevMonth}>‹</button>
              <span style={{ fontWeight:700, fontSize:16, minWidth:180, textAlign:'center' }}>
                {MONTHS[month]} {year}
              </span>
              <button className="btn-ghost" style={{ padding:'4px 12px', fontSize:17 }} onClick={nextMonth}>›</button>
            </div>
            <button className="btn-ghost" style={{ fontSize:12 }} onClick={goToday}>Today</button>
          </div>

          {/* Legend */}
          <div style={{ display:'flex', gap:16, marginBottom:12, flexWrap:'wrap' }}>
            {[{color:'#8FA85E',label:'Sales'},{color:'#D4A24C',label:'Purchases'},{color:'#CC7A3D',label:'Reminders'}].map(l=>(
              <div key={l.label} style={{ display:'flex', alignItems:'center', gap:5, fontSize:11, color:'var(--muted)' }}>
                <span style={{ width:8,height:8,borderRadius:'50%',background:l.color,display:'inline-block',flexShrink:0 }} />
                {l.label}
              </div>
            ))}
          </div>

          {/* Weekday row */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(7, 1fr)', gap:3, marginBottom:4 }}>
            {WEEKDAYS.map(d=>(
              <div key={d} style={{ textAlign:'center', fontSize:10, fontWeight:600, color:'var(--muted)',
                padding:'3px 0', textTransform:'uppercase', letterSpacing:'0.06em' }}>{d}</div>
            ))}
          </div>

          {/* Grid */}
          {loadingMonth
            ? <div style={{ textAlign:'center', padding:60 }}><div className="spinner" /></div>
            : (
              <div style={{ display:'grid', gridTemplateColumns:'repeat(7, 1fr)', gap:4 }}>
                {Array.from({ length: firstDayOfWeek }).map((_,i)=>(<div key={`e${i}`} />))}
                {Array.from({ length: daysInMonth },(_,i)=>i+1).map(day => {
                  const cellDate = new Date(year, month, day);
                  const isToday  = isSameLocalDay(cellDate, today);
                  const ind      = indicators[day] || {};
                  const hasAny   = ind.hasSales || ind.hasPurchases || ind.hasReminders;
                  return (
                    <button key={day} onClick={()=>setSelectedDay(cellDate)} style={{
                      aspectRatio:'1', borderRadius:8,
                      border: isToday ? '2px solid var(--accent)' : '1px solid var(--border)',
                      background: isToday ? 'rgba(204,122,61,0.12)' : 'var(--surface2)',
                      cursor:'pointer', display:'flex', flexDirection:'column', alignItems:'center',
                      justifyContent:'center', padding:'4px 2px', fontSize:13,
                      fontWeight: isToday ? 700 : 400,
                      color: isToday ? 'var(--accent)' : 'var(--text)',
                      transition:'background 0.12s, transform 0.1s',
                    }}
                    onMouseEnter={e=>{ e.currentTarget.style.background='rgba(204,122,61,0.20)'; e.currentTarget.style.transform='scale(1.05)'; }}
                    onMouseLeave={e=>{ e.currentTarget.style.background=isToday?'rgba(204,122,61,0.12)':'var(--surface2)'; e.currentTarget.style.transform='scale(1)'; }}
                    >
                      <span>{day}</span>
                      {hasAny && (
                        <div style={{ display:'flex', gap:2, marginTop:3 }}>
                          {ind.hasSales     && <Dot color="#8FA85E" />}
                          {ind.hasPurchases && <Dot color="#D4A24C" />}
                          {ind.hasReminders && <Dot color="#CC7A3D" />}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )
          }

          <p style={{ marginTop:14, fontSize:11, color:'var(--muted)', textAlign:'center' }}>
            Click any day to view transactions or set a reminder
          </p>
        </div>

        {/* ── RIGHT: Today's reminders sidebar ── */}
        <div className="card" style={{ display:'flex', flexDirection:'column', gap:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:14 }}>
            <NavIcon name="bell" size={16} />
            <div>
              <div style={{ fontWeight:700, fontSize:14 }}>Today's Reminders</div>
              <div style={{ fontSize:11, color:'var(--muted)' }}>
                {today.toLocaleDateString('en-GB',{ day:'numeric', month:'short', year:'numeric' })}
              </div>
            </div>
          </div>

          {/* Add form */}
          <form onSubmit={handleAddTodayReminder} style={{ display:'flex', gap:6, marginBottom:14 }}>
            <input
              className="input"
              style={{ flex:1, fontSize:12, padding:'7px 10px' }}
              placeholder="New reminder…"
              value={newReminderText}
              onChange={e=>setNewReminderText(e.target.value)}
              maxLength={200}
            />
            <button type="submit" className="btn btn-primary"
              style={{ fontSize:12, padding:'7px 12px', whiteSpace:'nowrap' }}
              disabled={addingTodayReminder || !newReminderText.trim()}>
              {addingTodayReminder ? '…' : '+'}
            </button>
          </form>

          {/* List */}
          {todayReminders.length === 0 ? (
            <div style={{ textAlign:'center', padding:'24px 0', color:'var(--muted)', fontSize:12 }}>
              No reminders for today.<br />
              <span style={{ fontSize:11 }}>Add one above or click a day on the calendar.</span>
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
              {todayReminders.map(r=>(
                <div key={r.id} style={{
                  display:'flex', justifyContent:'space-between', alignItems:'center',
                  padding:'9px 10px', background:'var(--surface2)', borderRadius:6,
                  borderLeft:'3px solid var(--accent)', fontSize:13,
                }}>
                  <span style={{ flex:1, marginRight:8, lineHeight:1.4 }}>{r.text}</span>
                  <button onClick={()=>handleDeleteTodayReminder(r.id)}
                    style={{ background:'none', border:'none', cursor:'pointer',
                      color:'var(--muted)', fontSize:13, padding:'1px 4px', lineHeight:1, borderRadius:3 }}
                    title="Dismiss">✕</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Day popup */}
      {selectedDay && (
        <DayPopup
          date={selectedDay}
          onClose={() => setSelectedDay(null)}
          onChanged={() => {
            loadMonth(year, month);
            loadTodayReminders();
          }}
        />
      )}
    </div>
  );
}
